import type { ApiClient, JobResult } from './api';

// Waiting for a job to finish.
//
// Jobs stopped being request-response when the write path moved onto Durable
// Functions: creating one returns 202 and an id, and the work carries on in the
// background. That is the honest shape — writing hundreds of records to
// somebody's ERP is not something to hold an HTTP connection open for — but it
// means every screen that starts a job now has to wait for it.
//
// One helper rather than three copies of a polling loop, because the three
// callers (grid edit, merge, undo) must agree on what "finished" means.

/** Statuses a job does not move on from. Anything else is still running. */
const TERMINAL = new Set(['completed', 'failed', 'undone']);

const POLL_INTERVAL_MS = 1500;

/**
 * How long to keep watching.
 *
 * Generous on purpose: the cap is 1000 changes, each record costs a read, a
 * write and a deliberate pace, and a slow instance can make that twenty
 * minutes. Giving up earlier would report a job as lost while it was still
 * writing — and the job would carry on regardless, which is the worst of both.
 */
const MAX_WAIT_MS = 45 * 60 * 1000;

export interface JobProgress {
  total: number;
  done: number;
  written: number;
  failed: number;
  skipped: number;
}

function progressOf(result: JobResult): JobProgress {
  const counted = (status: string) =>
    result.changes.filter((c) => c.writeStatus === status).length;
  const written = counted('written');
  const failed = counted('failed');
  const skipped = counted('skipped');
  return {
    total: result.changes.length,
    done: written + failed + skipped,
    written,
    failed,
    skipped,
  };
}

/**
 * Polls a job until it reaches a terminal state, reporting progress on the way.
 *
 * Returns the final job and its changes — the same shape the create call used
 * to return synchronously, so callers keep working the way they did.
 */
export async function awaitJob(
  api: ApiClient,
  tenantId: string,
  jobId: string,
  onProgress?: (progress: JobProgress) => void
): Promise<JobResult> {
  const startedAt = Date.now();

  for (;;) {
    const result = await api.get<JobResult>(`/tenants/${tenantId}/jobs/${jobId}`);
    onProgress?.(progressOf(result));

    if (TERMINAL.has(result.job.jobStatus)) return result;

    if (Date.now() - startedAt > MAX_WAIT_MS) {
      // Deliberately not an error about the job: the job is fine, we simply
      // stopped watching. Saying "failed" here would be a lie about the
      // customer's data.
      throw new Error(
        'This job is taking longer than expected and is still running. ' +
          'Its progress is in job history — nothing has been lost.'
      );
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
}
